import { __awaiter } from "tslib";
import { getSession } from '../kloans-authentication';
import { Auth } from './types';
import { camelKeys } from 'change-object-case';
import { startSessionTimeout, renewSessionTimeout, onSessionTimeoutExpire, deleteCurrentSessionTimeout } from "./SessionTimeout";
class SessionService {
    constructor() {
        this.authUser = null;
        this.plaidConfig = null;
        this.loginUrl = '/login/#/';
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            this.loginUrl = window.location.origin + '/login/#/';
            // Provide a way for init to tie the session back to the application
            return this.validateSession(() => { });
        });
    }
    /**
     * This is the actual method used within the user login which initialize the session.
     * The init method is ambiguous and currently used by the profile web only.
     */
    authenticate(authData, clientId) {
        localStorage.setItem(Auth.ID_TOKEN, authData.IdToken);
        localStorage.setItem(Auth.AUTH_ACCESS_TOKEN, authData.AccessToken);
        // Adding these two keys to sessionStorage for backwards compatibility with the old closure app.
        // Once we migrate it to vue, we should remove these setters here.
        sessionStorage.setItem(Auth.ID_TOKEN, authData.IdToken);
        sessionStorage.setItem('kauth_accesstoken', authData.AccessToken);
        // Caching a user object to local storage since our currently implementation is not keeping track of this
        // service instance. We will use this user cache to clean the cognito tokens at logout.
        const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
        const lastAuthUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);
        const authUserCache = {
            clientId: clientId,
            username: lastAuthUser,
            keyPrefix: keyPrefix,
        };
        localStorage.setItem(Auth.AUTH_USER, JSON.stringify(authUserCache));
        startSessionTimeout();
        onSessionTimeoutExpire(this.sessionExpired.bind(this));
    }
    // This will check for the token in the session storage for compatibility purposes. Once we migrate everything to vue
    // we no longer should check for the sessionStorage key
    isAuthenticated() {
        return localStorage.getItem(Auth.ID_TOKEN) !== null || sessionStorage.getItem(Auth.ID_TOKEN) !== null;
    }
    /**
     * The userAction param is to indicate whether the session expired or if the user is manually logging out.
     * When users manually logout, next time they login, they should get redirected to the default dashboard.
     */
    logout(userAction = false) {
        this.authUser = null;
        if (!userAction && window.location.href.indexOf('/login/') === -1) {
            sessionStorage.setItem(Auth.AUTH_CALLBACK, window.location.pathname + window.location.hash);
        }
        clearUserCache();
        deleteCurrentSessionTimeout();
        window.location.href = this.loginUrl;
    }
    sessionExpired() {
        clearUserCache();
        this.authUser = null;
        window.location.href = this.loginUrl;
    }
    validateSession(showLoader) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isAuthenticated()) {
                this.logout();
            }
            renewSessionTimeout();
            onSessionTimeoutExpire(this.sessionExpired.bind(this));
            // Create a promise so this is truly async.
            return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                if (!this.authUser) {
                    if (showLoader) {
                        showLoader(true);
                    }
                    try {
                        // camelCase session response because its still not updated on the backend.
                        const response = camelKeys(yield getSession(), { recursive: true });
                        this.authUser = response.data.data.user;
                        this.plaidConfig = response.data.data.plaid;
                        // Resolve with user and plaid data as a Session object.
                        resolve({ user: this.authUser, plaid: this.plaidConfig });
                        if (showLoader) {
                            showLoader(false);
                        }
                    }
                    catch (error) {
                        reject(error);
                        this.logout();
                    }
                }
            }));
        });
    }
    getUser() {
        return this.authUser;
    }
    getPlaidConfig() {
        return this.plaidConfig;
    }
    clearUser() {
        this.authUser = null;
    }
    static getInstance() {
        if (!SessionService.instance) {
            SessionService.instance = new SessionService();
        }
        return SessionService.instance;
    }
}
const clearUserCache = () => {
    // Our custom keys
    const user = JSON.parse(localStorage.getItem(Auth.AUTH_USER));
    localStorage.removeItem(Auth.AUTH_USER);
    localStorage.removeItem(Auth.ID_TOKEN);
    localStorage.removeItem(Auth.AUTH_ACCESS_TOKEN);
    sessionStorage.clear();
    if (!user) {
        return;
    }
    // Cognito Tokens
    const idTokenKey = `${user.keyPrefix}.${user.username}.idToken`;
    const accessTokenKey = `${user.keyPrefix}.${user.username}.accessToken`;
    const refreshTokenKey = `${user.keyPrefix}.${user.username}.refreshToken`;
    const lastUserKey = `${user.keyPrefix}.LastAuthUser`;
    const clockDriftKey = `${user.keyPrefix}.${user.username}.clockDrift`;
    localStorage.removeItem(idTokenKey);
    localStorage.removeItem(accessTokenKey);
    localStorage.removeItem(refreshTokenKey);
    localStorage.removeItem(lastUserKey);
    localStorage.removeItem(clockDriftKey);
};
export const sessionService = SessionService.getInstance();
